import { detectDeviceType, detectOS } from './device.ts';
import { formatTemplate, onTranslationsApplied, t } from './localization.ts';
import { hasSeenAnyPopup, onPopupSeen } from './popup.ts';
import { markProgressStep, ProgressStep } from './progress.ts';
import type { AbTestVariant, LocationData } from './types.ts';
import { escapeHtml } from './utils.ts';

let storyDeviceNoun: HTMLElement | null = null;
let storyReferrerSentence: HTMLElement | null = null;
let storyTime: HTMLElement | null = null;
let storyPopups: HTMLElement | null = null;
let storyButtonsList: HTMLElement | null = null;
let btnFetchIP: HTMLButtonElement | null = null;
let abTestButtons: HTMLButtonElement[] = [];
let abTestCopy: HTMLElement | null = null;
let accordions: HTMLElement[] = [];

let defaultLocationButtonText: string = '';
let loadingLocationButtonText: string = '';
const GUESS_LOCATION_LABEL_KEY = 'slides.dataTrail.guessLocationLabel';
const GUESS_LOCATION_LABEL_FALLBACK = 'Guess location';
const LOCATION_RESOLVED_CLASS = 'data-trail-location--resolved';
const LOCATION_MULTILINE_CLASS = 'data-trail-location--multiline';

let selectedAbVariant: AbTestVariant | null = null;
let sessionStart: number = Date.now();
let buttonClicks: Record<string, number> = Object.create(null);
let updateTimerId: number | null = null;
let documentClickHandlerBound: boolean = false;
let keydownHandlerBound: boolean = false;
let initialized: boolean = false;

function getReferrerHost(): string {
  const ref = document.referrer;
  if (!ref) return t('stats.referrer.direct', 'Direct');
  try {
    return new URL(ref).hostname;
  } catch {
    return t('stats.referrer.direct', 'Direct');
  }
}

function describeReferrerSentence(): string {
  const referrer = document.referrer;
  const host = getReferrerHost();
  const directLabel = t('stats.referrer.direct', 'Direct');
  const isDirect = !referrer || !host || host === directLabel;
  if (isDirect) {
    return t('slides.dataTrail.referrerDirect', 'directly');
  }
  const escapedHost = escapeHtml(host);
  return `<strong>${escapedHost}</strong>`;
}

function describeDevice(): string {
  const os = detectOS();
  const deviceType = detectDeviceType();

  if (deviceType === 'Mobile') {
    if (os === 'Android') return t('stats.device.androidPhone', 'an Android Phone');
    if (os === 'iOS') return t('stats.device.iphone', 'an iPhone');
    return t('stats.device.mobile', 'a mobile device');
  }

  if (deviceType === 'Tablet') {
    if (os === 'Android') return t('stats.device.androidTablet', 'an Android Tablet');
    if (os === 'iOS') return t('stats.device.ipad', 'an iPad');
    return t('stats.device.tablet', 'a tablet');
  }

  if (os === 'macOS') return t('stats.device.mac', 'a Mac');
  if (os === 'Windows') return t('stats.device.windows', 'a Windows PC');
  if (os && os !== 'Unknown') {
    return formatTemplate(t('stats.device.other', 'a {{os}} device'), { os });
  }
  return t('stats.device.generic', 'a device');
}

function splitArticleAndNoun(text: string): { article: string; noun: string } {
  if (!text) return { article: '', noun: '' };
  const match = text.match(/^(a|an)\s+/i);
  if (!match) return { article: '', noun: text };
  const article = match[0];
  const noun = text.slice(article.length);
  return { article, noun };
}

function updatePopupStats(): void {
  if (!storyPopups) return;
  const seenAny = hasSeenAnyPopup();
  const key = seenAny ? 'slides.dataTrail.popupSeen' : 'slides.dataTrail.popupNotSeen';
  const fallback = seenAny ? 'seen' : 'not seen';
  storyPopups.textContent = t(key, fallback);
}

function labelForButton(btn: HTMLButtonElement): string {
  return (
    btn.getAttribute('aria-label') ||
    btn.title ||
    (btn.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 40) ||
    t('slides.dataTrail.unlabeledButton', '(unlabeled button)')
  );
}

function recordButtonClick(btn: HTMLButtonElement | null): void {
  if (!btn) return;
  const isGuessLocation = btn === btnFetchIP;
  const isAbTestButton = abTestButtons.includes(btn);
  const isHookedSlot = btn.hasAttribute('data-slot');

  let label;
  if (isGuessLocation) {
    label = t(GUESS_LOCATION_LABEL_KEY, GUESS_LOCATION_LABEL_FALLBACK);
  } else if (isAbTestButton) {
    const variant = btn.dataset.variant;
    const key =
      variant === 'A' ? 'slides.abTesting.selectVariantA' : 'slides.abTesting.selectVariantB';
    const fallback = variant === 'A' ? 'Select variant A' : 'Select variant B';
    label = t(key, fallback);
  } else if (isHookedSlot) {
    const slotValueSpan = btn.querySelector('[data-slot-value]');
    label = slotValueSpan ? slotValueSpan.textContent.trim() : labelForButton(btn);
  } else {
    label = labelForButton(btn);
  }

  buttonClicks[label] = (buttonClicks[label] || 0) + 1;
  renderButtonClicks();
}

function renderButtonClicks(): void {
  if (!storyButtonsList) return;
  const entries = Object.entries(buttonClicks)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);
  if (!entries.length) {
    resetButtonList();
    return;
  }

  storyButtonsList.innerHTML = entries
    .map(([label, count]) => `<li>${escapeHtml(label)}: ${escapeHtml(String(count))}</li>`)
    .join('');
}

function resetButtonList(): void {
  if (!storyButtonsList) return;
  const defaultText =
    storyButtonsList.dataset.defaultText || t('slides.dataTrail.noneYet', 'None yet');
  storyButtonsList.innerHTML = `<li>${defaultText}</li>`;
}

function setLocationDisplay(place: string, connector: string, isp: string): void {
  if (!btnFetchIP) return;
  const hasContent = Boolean(place) || Boolean(isp);
  if (!hasContent) {
    btnFetchIP.textContent =
      defaultLocationButtonText ||
      t('slides.dataTrail.guessButton', 'Want me to guess your location?*');
    btnFetchIP.classList.remove(LOCATION_RESOLVED_CLASS);
    btnFetchIP.classList.remove(LOCATION_MULTILINE_CLASS);
    return;
  }

  let connectionPart = '';
  if (connector && isp) {
    const prefersMultiline = window.matchMedia('(max-width: 640px)').matches;
    if (prefersMultiline) {
      connectionPart = `\n${connector}${isp}`;
      btnFetchIP.classList.add(LOCATION_MULTILINE_CLASS);
    } else {
      connectionPart = ` | ${connector}${isp}`;
      btnFetchIP.classList.remove(LOCATION_MULTILINE_CLASS);
    }
  } else {
    btnFetchIP.classList.remove(LOCATION_MULTILINE_CLASS);
  }

  const displayText = [place, connectionPart].filter(Boolean).join('');
  btnFetchIP.textContent = displayText || defaultLocationButtonText;
  btnFetchIP.classList.add(LOCATION_RESOLVED_CLASS);
}

function resetLocationDisplay(): void {
  setLocationDisplay('', '', '');
}

function cleanISPName(value: string): string {
  if (!value) return '';
  return value
    .replace(/^AS\d+\s+/i, '')
    .replace(/\s+\(?AS\d+\)?$/i, '')
    .replace(/\s+AS\b/i, '')
    .trim();
}

function formatConnectionParts(data: LocationData): { connector: string; isp: string } {
  const rawOrg = data.org || '';
  const isp = cleanISPName(rawOrg) || t('stats.isp.unknown', 'an unknown network');
  const connector = isp ? t('slides.dataTrail.connectingThrough', 'connection: ') : '';
  return { connector, isp };
}

async function fetchIpLocation(): Promise<void> {
  if (!btnFetchIP) return;
  try {
    btnFetchIP.disabled = true;
    btnFetchIP.textContent = loadingLocationButtonText;
    btnFetchIP.classList.add('is-loading');
    const res = await fetch('https://ipapi.co/json/');
    if (!res.ok) throw new Error('IP service unavailable');
    const data = await res.json();
    const city = data.city || '';
    const region = data.region || '';
    const country = data.country_name || data.country || '';
    const placeParts = [city, region, country].filter(Boolean);
    const place = placeParts.length
      ? placeParts.join(', ')
      : t('stats.location.unknown', 'Unknown location');
    const { connector, isp } = formatConnectionParts(data);
    setLocationDisplay(place, connector, isp);
  } catch (err) {
    const place = t(
      'stats.location.unavailable',
      "Location unavailable - sorry, doesn't always work:("
    );
    setLocationDisplay(place, '', '');
  } finally {
    if (btnFetchIP) {
      btnFetchIP.disabled = false;
      btnFetchIP.classList.remove('is-loading');
      if (!btnFetchIP.textContent) {
        btnFetchIP.textContent = defaultLocationButtonText;
        btnFetchIP.classList.remove(LOCATION_RESOLVED_CLASS);
      }
    }
  }
}

function updateTimeOnSite(): void {
  if (!storyTime) return;
  const total = Math.floor((Date.now() - sessionStart) / 1000);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const formatted =
    (hours ? `${hours}:` : '') +
    `${minutes}`.padStart(2, '0') +
    ':' +
    `${seconds}`.padStart(2, '0');
  storyTime.textContent = formatted;
}

function getAbTestCopy(variant: AbTestVariant): string {
  if (variant === 'A') {
    return t(
      'slides.abTesting.copyA',
      "Split 100,000 visitors - half see A, half see B. Let the wallets vote.<br>That's called A/B testing."
    );
  }
  if (variant === 'B') {
    return t(
      'slides.abTesting.copyB',
      'Randomly show half the visitors A others B. Which copy gets more sales remains. Digital survival of the fittest aka A/B testing.'
    );
  }
  return '';
}

function setAbTestSelection(variant: AbTestVariant | null): void {
  selectedAbVariant = variant || null;
  abTestButtons.forEach((btn) => {
    const isActive = variant !== null && btn.dataset.variant === variant;
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });
  if (abTestCopy) {
    const copy = variant ? getAbTestCopy(variant) : '';
    abTestCopy.innerHTML = copy;
  }
  if (variant) {
    markProgressStep(ProgressStep.AB_TEST_CHOICE);
  }
}

function initAccordion(accordion: HTMLElement): void {
  const triggers = Array.from(accordion.querySelectorAll('[data-accordion-trigger]'));
  const panels = Array.from(accordion.querySelectorAll('[data-accordion-panel]'));

  function closeAll(exceptId: string | null): void {
    triggers.forEach((trigger: Element) => {
      const controls = trigger.getAttribute('aria-controls');
      const isMatch = controls === exceptId;
      trigger.setAttribute('aria-expanded', isMatch ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      if (panel.id === exceptId) {
        (panel as HTMLElement).hidden = false;
      } else {
        (panel as HTMLElement).hidden = true;
      }
    });
  }

  triggers.forEach((trigger: Element) => {
    trigger.addEventListener('click', () => {
      const controls = trigger.getAttribute('aria-controls');
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      if (isOpen) {
        trigger.setAttribute('aria-expanded', 'false');
        panels.forEach((panel: Element) => {
          if (panel.id === controls) (panel as HTMLElement).hidden = true;
        });
        return;
      }

      closeAll(controls);
    });
  });
}

function handleDocumentClick(event: MouseEvent): void {
  const btn = (event.target as HTMLElement).closest('button');
  if (!btn) return;
  if (btn === btnFetchIP) return;
  recordButtonClick(btn);
}

function handleKeydown(event: KeyboardEvent): void {
  if (event.defaultPrevented) return;
  if (event.key !== 'Enter' || event.repeat) return;
  if (event.ctrlKey || event.altKey || event.metaKey || event.shiftKey) return;
  const target = event.target as HTMLElement | null;
  if (!target) return;
  const tagName = target?.tagName;
  if (target.isContentEditable) return;
  if (
    tagName === 'TEXTAREA' ||
    tagName === 'INPUT' ||
    tagName === 'SELECT' ||
    tagName === 'BUTTON'
  ) {
    return;
  }
  btnFetchIP?.click();
}

function initStaticStats(): void {
  const devicePhrase = describeDevice();
  const { noun } = splitArticleAndNoun(devicePhrase);
  if (storyDeviceNoun) storyDeviceNoun.textContent = noun || '—';
  if (storyReferrerSentence) storyReferrerSentence.innerHTML = describeReferrerSentence();
  resetLocationDisplay();
  resetButtonList();
  renderButtonClicks();
  updatePopupStats();
}

function initDataTrail(): void {
  if (initialized) return;

  const dataTrailSection = document.getElementById('data');
  if (!dataTrailSection) {
    window.requestAnimationFrame(initDataTrail);
    return;
  }

  storyDeviceNoun = dataTrailSection.querySelector('#storyDeviceNoun');
  storyReferrerSentence = dataTrailSection.querySelector('#storyReferrerSentence');
  storyTime = dataTrailSection.querySelector('#storyTime');
  storyPopups = dataTrailSection.querySelector('#storyPopups');
  storyButtonsList = dataTrailSection.querySelector('#storyButtonsList');
  btnFetchIP = dataTrailSection.querySelector('#btnFetchIP');
  abTestButtons = Array.from(document.querySelectorAll('.ab-test-button'));
  abTestCopy = document.getElementById('abTestCopy');
  accordions = Array.from(document.querySelectorAll('[data-accordion]'));

  const essentialElements = [
    storyDeviceNoun,
    storyReferrerSentence,
    storyTime,
    storyPopups,
    storyButtonsList,
  ];
  const essentialsReady = essentialElements.every(Boolean);
  if (!essentialsReady) {
    window.requestAnimationFrame(initDataTrail);
    return;
  }

  defaultLocationButtonText =
    btnFetchIP?.dataset.defaultText ||
    t('slides.dataTrail.guessButton', 'Want me to guess your location?*');
  loadingLocationButtonText =
    btnFetchIP?.dataset.loadingText || t('slides.dataTrail.guessLocationLoading', '...');

  sessionStart = Date.now();
  buttonClicks = Object.create(null);

  initStaticStats();

  if (!updateTimerId) {
    updateTimeOnSite();
    updateTimerId = window.setInterval(updateTimeOnSite, 1000);
  }

  if (!documentClickHandlerBound) {
    document.addEventListener('click', handleDocumentClick);
    documentClickHandlerBound = true;
  }

  if (!keydownHandlerBound) {
    document.addEventListener('keydown', handleKeydown);
    keydownHandlerBound = true;
  }

  btnFetchIP?.addEventListener('click', (event) => {
    event.preventDefault();
    recordButtonClick(btnFetchIP);
    markProgressStep(ProgressStep.GUESS_LOCATION);
    fetchIpLocation();
  });

  abTestButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const variant = btn.dataset.variant as AbTestVariant | undefined;
      if (!variant) return;
      if (selectedAbVariant === variant) return;
      setAbTestSelection(variant);
    });
  });

  setAbTestSelection(null);

  accordions.forEach((accordion) => initAccordion(accordion));

  onPopupSeen(updatePopupStats);
  onTranslationsApplied(() => {
    defaultLocationButtonText =
      btnFetchIP?.dataset.defaultText ||
      t('slides.dataTrail.guessButton', 'Want me to guess your location?*');
    loadingLocationButtonText =
      btnFetchIP?.dataset.loadingText || t('slides.dataTrail.guessLocationLoading', '...');
    if (
      btnFetchIP &&
      !btnFetchIP.classList.contains(LOCATION_RESOLVED_CLASS) &&
      !btnFetchIP.classList.contains('is-loading')
    ) {
      btnFetchIP.textContent = defaultLocationButtonText;
    }
    renderButtonClicks();
    updatePopupStats();
    if (selectedAbVariant) {
      setAbTestSelection(selectedAbVariant);
    }
  });

  initialized = true;
}

export { initDataTrail };
